import React, { useEffect } from 'react';
import { CheckCircle2, Shield, Clock, Award, Hammer, Users } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: <Shield className="w-8 h-8 text-orange-600" />,
      title: "Garanti for kvalitet",
      description: "Jeg står 100% inde for mit arbejde og leverer altid resultater, der holder i mange år."
    },
    {
      icon: <Clock className="w-8 h-8 text-orange-600" />,
      title: "Altid til tiden",
      description: "Jeg ved, at din tid er vigtig. Derfor overholder jeg alle aftaler og tidsplaner til punkt og prikke."
    },
    {
      icon: <Award className="w-8 h-8 text-orange-600" />,
      title: "Ekspertise & præcision",
      description: "Jeg går op i høj kvalitet og komponerede løsninger med natur- og betonsten, skræddersyet til dine ønsker."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://assets.auxodev.dk/assets/66803e0c-9723-4cfe-96f2-7ea52c9dabed/c9bc5107-9e56-4f64-acbc-03df9bb7eec1.jpg" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">Om Nielsen Brolægning</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Passion for brolægning og kærlighed til det gode håndværk er fundamentet for alt mit arbejde.
          </p>
        </div>
      </section>

      {/* History & Mission */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-orange-600 font-bold tracking-wider uppercase text-sm mb-3 text-left">Min Historie</h2>
              <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-left">Fra passion til <span className="text-orange-600">profession</span></h3>
              <div className="space-y-6 text-slate-600 text-lg leading-relaxed text-left">
                <p>
                  Det hele startede med en enkelt trillebør og en vision om at levere brolægning, der ikke bare ser godt ud, men som også holder i generationer. Nielsen Brolægning er stiftet af mig, Daniel Nielsen, med et ønske om at bringe det klassiske brolæggerhåndværk i øjenhøjde med kunden.
                </p>
                <p>
                  I dag driver jeg virksomheden som en enkeltmandsvirksomhed, hvor jeg selv står for hele processen – fra den indledende rådgivning til det færdige resultat. Mine kerneværdier er ærlighed, punktlighed og kompromisløs kvalitet, og jeg har specialiseret mig i at komponere unikke løsninger med natur- og betonsten.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://assets.auxodev.dk/assets/66803e0c-9723-4cfe-96f2-7ea52c9dabed/89e6d88b-15e9-4736-ad5b-8d20f155e9b5.jpg" 
                  alt="Nielsen Brolægning Kvalitetsarbejde" 
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-10 -right-10 bg-orange-600 p-8 rounded-2xl shadow-xl hidden md:block">
                <div className="flex items-center gap-4 text-white">
                  <Award className="w-12 h-12" />
                  <div>
                    <p className="text-3xl font-bold uppercase tracking-tight">Kvalitet</p>
                    <p className="text-orange-100 font-medium">Uden kompromis</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Hvorfor vælge mig?</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Jeg bygger min virksomhed på tillid, personlig dialog og langsigtede relationer med mine kunder.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 text-left">
                <div className="mb-6 bg-orange-50 w-16 h-16 flex items-center justify-center rounded-xl">
                  {value.icon}
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h4>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 rounded-3xl p-12 md:p-20 relative overflow-hidden text-center">
            <div className="relative z-10 max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">Skal jeg hjælpe dig med dit næste projekt?</h2>
              <p className="text-slate-300 text-lg mb-10">
                Jeg kommer gerne forbi til en uforpligtende snak om dine muligheder og giver et fast tilbud på opgaven.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/#contact" className="px-8 py-4 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition-all text-center">
                  Kontakt mig i dag
                </a>
                <a href="tel:+4542361065" className="px-8 py-4 border-2 border-white/20 text-white rounded-lg font-bold hover:bg-white/10 transition-all text-center">
                  Ring: +45 42 36 10 65
                </a>
              </div>
            </div>
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
